import React from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { EventCard, ClubCard, MemberCard } from '../components/ui/Cards';
import SafeIcon from '../common/SafeIcon';
import SolDivider from '../components/ui/SolDivider';
import { motion } from 'framer-motion';

export default function Inicio() {
  const { currentUser, eventos, clubs, trainings, members, CAPITULOS, rsvps, toggleRsvp } = useApp();

  // Pick top preview data
  const upcomingEventos = eventos.filter(e => !e.isPast).slice(0, 2);
  const featuredClubs = clubs.slice(0, 3);
  const recentTrainings = trainings.filter(t => t.status === 'approved').slice(0, 2);
  
  // Exclude current user from new members preview
  const newMembers = members.filter(m => m.status === 'approved' && m.id !== currentUser.id).slice(0, 3);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-16">
      
      {/* Welcome Header */}
      <div className="relative">
        <div className="absolute -inset-x-10 -top-10 h-[200px] bg-gradient-to-b from-[#74ACDF]/10 to-transparent -z-10 rounded-b-[100px]" />
        <div className="pt-4 md:pt-8">
          <span className="label-technical text-[#F6B40E] mb-2 block">Inicio</span>
          <h1 className="text-4xl md:text-5xl font-editorial font-bold text-[#1B2A4E] leading-tight">
            Hola, <span className="text-[#74ACDF] italic">{currentUser.nombre.split(' ')[0]}</span> 👋
          </h1>
          <p className="text-lg text-stone-500 mt-3 max-w-lg">
            Te damos la bienvenida al Movimiento. Acá tenés un resumen de lo último en la comunidad.
          </p>
        </div>
      </div>

      {/* Próximos Encuentros Strip */}
      <section>
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="text-2xl font-editorial font-bold text-[#1B2A4E]">Próximos Encuentros</h2>
            <p className="text-sm text-stone-500 mt-1">Conectá con otros miembros en persona o virtualmente.</p>
          </div>
          <Link to="/eventos" className="hidden md:flex items-center gap-2 text-[#74ACDF] font-bold text-sm hover:text-[#1B2A4E] transition-colors label-technical">
            Ver agenda completa <SafeIcon name="ArrowRight" className="w-4 h-4" />
          </Link>
        </div>
        <div className="space-y-4">
          {upcomingEventos.length > 0 ? upcomingEventos.map((e, idx) => (
            <EventCard 
              key={e.id} 
              e={e} 
              delay={idx * 0.1} 
              capituloName={CAPITULOS.find(c => c.id === e.capitulo_id)?.nombre} 
              isAttending={rsvps.some(r => r.eventId === e.id && r.memberId === currentUser.id)} 
              toggleRsvp={toggleRsvp} 
              currentUser={currentUser} 
            />
          )) : (
            <div className="bg-stone-50 rounded-2xl p-8 text-center border border-stone-100 text-stone-500 italic">No hay encuentros próximos por el momento.</div>
          )}
        </div>
        <Link to="/eventos" className="md:hidden mt-4 inline-flex items-center justify-center w-full gap-2 bg-stone-100 text-[#1B2A4E] font-bold py-3 rounded-xl text-sm transition-colors">
          Ver agenda completa <SafeIcon name="ArrowRight" className="w-4 h-4" />
        </Link>
      </section>

      <SolDivider />

      {/* Clubs Destacados Strip */}
      <section>
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="text-2xl font-editorial font-bold text-[#1B2A4E]">Clubs Destacados</h2>
            <p className="text-sm text-stone-500 mt-1">Espacios temáticos recurrentes de la comunidad.</p>
          </div>
          <Link to="/clubs" className="hidden md:flex items-center gap-2 text-[#74ACDF] font-bold text-sm hover:text-[#1B2A4E] transition-colors label-technical">
            Explorar clubes <SafeIcon name="ArrowRight" className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {featuredClubs.map((club, idx) => (
            <motion.div key={club.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}>
              <ClubCard club={club} capituloName={CAPITULOS.find(c => c.id === club.capitulo_id)?.nombre} />
            </motion.div>
          ))}
        </div>
        <Link to="/clubs" className="md:hidden mt-4 inline-flex items-center justify-center w-full gap-2 bg-stone-100 text-[#1B2A4E] font-bold py-3 rounded-xl text-sm transition-colors">
          Explorar clubes <SafeIcon name="ArrowRight" className="w-4 h-4" />
        </Link>
      </section>

      {/* Nuevo en la Academia Strip */}
      <section className="bg-white p-8 md:p-10 rounded-[40px] border border-stone-100 shadow-sm relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#74ACDF]/5 rounded-full -mr-20 -mt-20 blur-3xl pointer-events-none" />
        <div className="flex items-end justify-between mb-8 relative z-10">
          <div>
            <span className="label-technical text-[#F6B40E] mb-2 block">Academia</span>
            <h2 className="text-2xl font-editorial font-bold text-[#1B2A4E]">Últimas Capacitaciones</h2>
          </div>
          <Link to="/academia" className="hidden md:flex items-center gap-2 text-[#74ACDF] font-bold text-sm hover:text-[#1B2A4E] transition-colors label-technical">
            Ir a la Academia <SafeIcon name="ArrowRight" className="w-4 h-4" />
          </Link>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
          {recentTrainings.map((t, idx) => (
            <motion.div 
              key={t.id}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="flex gap-6 p-5 rounded-[24px] border border-stone-100 shadow-sm hover:shadow-xl transition-all duration-300 bg-[#FAF9F6] group"
            >
              <div className="w-28 h-28 shrink-0 rounded-[16px] overflow-hidden relative shadow-md">
                <img src={t.embed_url} alt={t.titulo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-[#1B2A4E]/30 flex items-center justify-center group-hover:bg-[#1B2A4E]/10 transition-colors">
                  <div className="w-10 h-10 bg-[#F6B40E] rounded-full flex items-center justify-center shadow-lg">
                    <SafeIcon name="Play" className="w-4 h-4 text-[#1B2A4E] ml-0.5" />
                  </div>
                </div>
              </div>
              <div className="flex-1 py-1 flex flex-col justify-center">
                <div className="label-technical text-[#74ACDF] mb-2">{t.categoria}</div>
                <h3 className="font-editorial font-bold text-lg text-[#1B2A4E] leading-tight mb-2 group-hover:text-[#74ACDF] transition-colors line-clamp-2">{t.titulo}</h3>
                <div className="text-xs text-stone-400 font-bold tracking-wider uppercase">{t.duracion} • Exclusivo Miembros</div>
              </div>
            </motion.div>
          ))}
        </div>
        <Link to="/academia" className="md:hidden mt-6 inline-flex items-center justify-center w-full gap-2 bg-stone-50 text-[#1B2A4E] font-bold py-3 rounded-xl text-sm transition-colors border border-stone-200">
          Ir a la Academia <SafeIcon name="ArrowRight" className="w-4 h-4" />
        </Link>
      </section>

      {/* Nuevos en la Comunidad Strip */}
      <section>
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="text-2xl font-editorial font-bold text-[#1B2A4E]">Nuevos en la Comunidad</h2>
            <p className="text-sm text-stone-500 mt-1">Conocé a los últimos miembros en sumarse.</p>
          </div>
          <Link to="/directorio" className="hidden md:flex items-center gap-2 text-[#74ACDF] font-bold text-sm hover:text-[#1B2A4E] transition-colors label-technical">
            Ver directorio <SafeIcon name="ArrowRight" className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {newMembers.map((member, idx) => (
            <motion.div key={member.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}>
              <MemberCard member={member} capituloName={CAPITULOS.find(c => c.id === member.capitulo_id)?.nombre} />
            </motion.div>
          ))}
        </div>
        <Link to="/directorio" className="md:hidden mt-4 inline-flex items-center justify-center w-full gap-2 bg-stone-100 text-[#1B2A4E] font-bold py-3 rounded-xl text-sm transition-colors">
          Ver directorio <SafeIcon name="ArrowRight" className="w-4 h-4" />
        </Link>
      </section>

    </motion.div>
  );
}